import React, { useContext } from "react";
import { useStore } from "../../store/hooks/use-store";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { PageContext, usePageContext } from "../../global/page-context";
import { SwagSlugBuilder } from "@swo/shared";
import { NewHeaderData } from "@swo/storefront";
import { SwarovskiHomepageLink } from "./subcomponents/SwarovskiLogoComponent";
import { ContextLessHeaderComponent, HeaderHooks } from "./ContextLessHeaderComponent";
import { t2 } from "../../domain/translations/translations";
import { HeaderT } from "./HeaderT";

const HeaderComponent: ReactTFC<{
  header: NewHeaderData;
  version: "normal" | "simplified" | "minimalistic";
  floatingDisabled?: boolean;
  headerHooks?: HeaderHooks;
}> = ({ floatingDisabled, headerHooks, header, version }) => {
  const { pageBase } = usePageContext();
  const store = useStore();
  const { links, ...propsRest } = header;

  const floatingDisabledWithStoreOverride = floatingDisabled || store.state.headerFloatingDisabled;

  const homepageLink: SwarovskiHomepageLink = {
    isInternal: true,
    localizedHomepage: SwagSlugBuilder.segmentPathObj(pageBase.locale, pageBase.segment),
  };

  const { links: ctxLinks } = useContext(PageContext);

  const linksMapped = {
    homepage: homepageLink,
    cartPage: links.linkToCart ?? null,
    dealerLocatorPage: links.dealerLocatorLink,
    searchPage: links.searchPageLink,
    user: {
      profile: ctxLinks.myServiceProfile,
      login: ctxLinks.myServiceLogin,
      register: ctxLinks.myServiceRegister,
    },
  };
  const ht = header.translations;

  const t: HeaderT = t2({ ...ht.languagePicker, ...ht.general, ...ht.header, ...ht.search });

  // Keep all context, history, store related subscriptions in this parent component
  // and only pass used data downstream.
  // Otherwise, Header is prone to often be re-rendered
  // and because it's quite heavy, there's a non-trivial performance hit.
  return (
    <ContextLessHeaderComponent
      t={t}
      header={header}
      headerHooks={headerHooks}
      locale={pageBase.locale}
      segment={pageBase.segment}
      links={linksMapped}
      floatingDisabled={floatingDisabledWithStoreOverride}
      version={version}
      contactData={propsRest.contactData}
      languagePicker={propsRest.languagePicker}
      segmentSelector={propsRest.segmentSelector}
      theme={propsRest.theme}
      quickLinks={header.quickLinks}
    />
  );
};

const MemoHeaderComponent = React.memo(HeaderComponent);

export { MemoHeaderComponent as HeaderComponent };
